import { User } from '@/types';
import { Page } from '@/types/pageSchema';
import { PageLocation, Section } from '@/types/store';
import { ItemType, PageItem, SectionItem, StoreItem } from '../types/store';
import { isSection } from './diffState.logic';
import { renumberAndSortItems } from './sort.logic';

/**
 * Converts a Page to a PageItem for the store.
 */
const pageToStoreItem = (page: Page, user: User): PageItem => {
    return {
        id: page.page_id,
        status: page.status,
        store_id: page.store_id,
        order: page.order,
        type: ItemType.PAGE,
        data: page,
        user: user,
    };
};

/**
 * Converts a Section to a SectionItem for the store.
 * The Section already has the correct type (SectionType), so it maps directly.
 */
const sectionToStoreItem = (section: Section): SectionItem => {
    return {
        id: section.section_id,
        status: section.status,
        store_id: section.store_id,
        order: section.order,
        type: section.type,
        data: section.data,
    } as SectionItem;
};

/**
 * Converts a Page or Section to a StoreItem.
 */
export const toStoreItem = (item: Page | Section, user: User): StoreItem | null => {
    if ('page_id' in item) {
        return pageToStoreItem(item, user);
    }
    if ('section_id' in item) {
        return sectionToStoreItem(item);
    }
    return null;
};

/**
 * Converts arrays of Pages and Sections to a unified StoreItem array.
 * Filters out landing pages and sorts by order.
 */
export const toStoreItems = (pages: Page[], sections: Section[], user: User): StoreItem[] => {
    const nonLandingPages = pages.filter(p => p.data.location !== PageLocation.LANDING);

    // Convert pages to store items
    const pageItems: StoreItem[] = nonLandingPages.map(page => pageToStoreItem(page, user));

    // Convert sections to store items
    const sectionItems: StoreItem[] = sections.map(sectionToStoreItem);

    // Combine and normalize order
    const storeItems = renumberAndSortItems([...pageItems, ...sectionItems]);
    return storeItems;
};

/**
 * Generates a new section ID as a random positive integer that does not
 * collide with any existing section ID in storeItems.
 */
export const generateNewSectionId = (storeItems: StoreItem[]): number => {
    const existingIds = new Set(
        storeItems
            .filter((item): item is SectionItem => isSection(item) && typeof item.id === 'number')
            .map(item => Number(item.id))
    );

    let id: number;
    do {
        id = Math.floor(Math.random() * 1000000) + 1;
    } while (existingIds.has(id));
    return id;
};
